"use client";

import type {
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
} from "@stockline/shared/types/sync";
import type { ProposalBatch } from "@stockline/shared/types/ai/proposal";
import { adaptV2PreOrder } from "./pre-order-adapter";

function getV2ApiUrl(): string {
  if (import.meta.env.VITE_V2_API_URL) {
    return import.meta.env.VITE_V2_API_URL;
  }
  // Production: V2 API is reverse-proxied at /api/v2/* by the frontend nginx,
  // so use relative URLs. `api.<host>` is the FastAPI backend, not Hono.
  if (
    globalThis.window !== undefined &&
    (globalThis.location.hostname.endsWith("stockline.net") ||
      globalThis.location.hostname.endsWith("stockline.ai"))
  ) {
    return "";
  }
  if (import.meta.env.DEV) {
    return "http://localhost:3001";
  }
  return `http://${globalThis.location.hostname}:3001`;
}

export const V2_API_URL = getV2ApiUrl();

function getToken(): string | null {
  if (globalThis.window !== undefined) {
    return localStorage.getItem("bearer");
  }
  return null;
}

export async function v2Fetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (options?.body && typeof options.body === "string") {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(`${V2_API_URL}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text();
    const err = new Error(`V2 API error ${res.status}: ${text}`);
    (err as any).status = res.status;
    try {
      (err as any).body = JSON.parse(text);
    } catch {
      // not JSON
    }
    throw err;
  }

  return res.json();
}

export class DuplicatePreOrderError extends Error {
  /**
   * Number of pre-orders currently colliding on the same
   * (partner, delivery_date). Always `>= 1` when this error is thrown.
   *
   * When `count === 1`, the recap can focus `existingPreOrder` directly.
   * When `count > 1`, the recap filters on (partnerId, deliveryDate) so the
   * user picks the right order visually — the backend deliberately does
   * not ship the full list of rows because the recap already loads them
   * via its normal data path once filtered.
   */
  public readonly count: number;

  constructor(
    message: string,
    public existingPreOrder: Record<string, unknown>,
    count?: number,
  ) {
    super(message);
    this.name = "DuplicatePreOrderError";
    // Default to 1 so older API responses (which don't ship `count`) still
    // produce the legacy single-PO focus behaviour rather than triggering
    // the filter branch.
    this.count = typeof count === "number" && count >= 1 ? count : 1;
  }
}

export interface BatchMergeHeaderDiffEntry {
  field: string;
  values: Array<{
    preOrderId: string;
    preOrderNumber: string | null;
    value: unknown;
  }>;
}

export class BatchMergeError extends Error {
  constructor(
    public code:
      | "TOO_FEW_SOURCES"
      | "DUPLICATE_IDS"
      | "NOT_FOUND"
      | "INVALID_STATUS"
      | "HEADER_MISMATCH"
      | "ID_COLLISION",
    message: string,
    public details: Record<string, unknown> | null,
  ) {
    super(message);
    this.name = "BatchMergeError";
  }
}

export const v2Api = {
  chatSessions: {
    changes(params?: { since?: number; limit?: number }) {
      const query = new URLSearchParams();
      if (params?.since != null) query.set("since", String(params.since));
      if (params?.limit) query.set("limit", String(params.limit));
      const qs = query.toString();
      const path = qs
        ? `/api/v2/chat-sessions/changes?${qs}`
        : "/api/v2/chat-sessions/changes";
      return v2Fetch<SyncPullResponse<any>>(path);
    },
    pushChanges(body: SyncPushRequest<any>) {
      return v2Fetch<SyncPushResponse<any>>(`/api/v2/chat-sessions/changes`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
  },

  chatMessages: {
    changes(params?: { since?: number; limit?: number; sessionId?: string }) {
      const query = new URLSearchParams();
      if (params?.since != null) query.set("since", String(params.since));
      if (params?.limit) query.set("limit", String(params.limit));
      if (params?.sessionId) query.set("sessionId", params.sessionId);
      const qs = query.toString();
      const path = qs
        ? `/api/v2/chat-messages/changes?${qs}`
        : "/api/v2/chat-messages/changes";
      return v2Fetch<SyncPullResponse<any>>(path);
    },
    pushChanges(body: SyncPushRequest<any>) {
      return v2Fetch<SyncPushResponse<any>>(`/api/v2/chat-messages/changes`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
  },

  executions: {
    trigger(body: {
      items: Array<{
        channel: "email" | "sms" | "whatsapp";
        id: string;
      }>;
      workflowId: string;
      instructions?: string;
    }) {
      return v2Fetch<{
        succeeded: Array<{
          chat_id: string;
          execution_id?: string;
          status?: string;
        }>;
        failed: Array<{ chat_id: string; error?: string }>;
        workflow_id?: string;
        workflow_name?: string;
      }>("/api/v2/executions/trigger", {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
  },

  chatExecutions: {
    changes(params?: { since?: number; limit?: number; sessionId?: string }) {
      const query = new URLSearchParams();
      if (params?.since != null) query.set("since", String(params.since));
      if (params?.limit) query.set("limit", String(params.limit));
      if (params?.sessionId) query.set("sessionId", params.sessionId);
      const qs = query.toString();
      const path = qs
        ? `/api/v2/chat-executions/changes?${qs}`
        : "/api/v2/chat-executions/changes";
      return v2Fetch<SyncPullResponse<any>>(path);
    },
    cancel(executionId: string) {
      return v2Fetch<{ message: string }>(
        `/api/v2/chat-executions/${executionId}/cancel`,
        { method: "POST" },
      );
    },
  },

  proposals: {
    changes(params?: { since?: number; limit?: number; status?: number }) {
      const query = new URLSearchParams();
      if (params?.since != null) query.set("since", String(params.since));
      if (params?.limit) query.set("limit", String(params.limit));
      if (params?.status != null) query.set("status", String(params.status));
      const qs = query.toString();
      const path = qs
        ? `/api/v2/ai-proposals/changes?${qs}`
        : "/api/v2/ai-proposals/changes";
      return v2Fetch<SyncPullResponse<ProposalBatch>>(path);
    },
    getAll(params?: { status?: number }) {
      const query = new URLSearchParams();
      if (params?.status != null) query.set("status", String(params.status));
      const qs = query.toString();
      const path = qs ? `/api/v2/ai-proposals?${qs}` : "/api/v2/ai-proposals";
      return v2Fetch<SyncPullResponse<ProposalBatch>>(path);
    },
    getBatch(batchId: string) {
      return v2Fetch<ProposalBatch>(`/api/v2/ai-proposals/${batchId}`);
    },
    byEntity(entityType: string, entityId: string) {
      return v2Fetch<ProposalBatch[]>(
        `/api/v2/ai-proposals/by-entity/${entityType}/${entityId}`,
      );
    },
    async getSourceDocument(batchId: string): Promise<{
      mediaId: string;
      name: string;
      mimeType: string | null;
      url: string;
    } | null> {
      try {
        return await v2Fetch<{
          mediaId: string;
          name: string;
          mimeType: string | null;
          url: string;
        }>(`/api/v2/ai-proposals/${batchId}/source-document`);
      } catch (err: unknown) {
        if (
          typeof err === "object" &&
          err !== null &&
          (err as { status?: number }).status === 404
        ) {
          return null;
        }
        throw err;
      }
    },
  },

  // `aiPreOrders` namespace removed — accept AND decline are both
  // resolved client-side via `resolveAiLocally` and shipped through
  // `push-batch`. There are no public `/approve` or `/decline` endpoints
  // anymore; the AI-aware `/api/v2/ai-pre-orders/internal/*` endpoints
  // still exist for AI-service → V2 staging (X-Internal-Token only).

  preOrders: {
    async create(body: {
      id?: string;
      createdAt?: string | null;
      sourceType?: number;
      status?: number;
      type?: number;
      category?: number;
      partnerId?: string | null;
      shippingAddressId?: string | null;
      locationId?: string | null;
      buyerId?: string | null;
      employeeId?: string | null;
      groupId?: string | null;
      deliveryDate?: string | null;
      orderDate?: string | null;
      comment?: string | null;
      preOrderNumber?: string | null;
      customerPoNumber?: string | null;
      createInitialFlow?: boolean;
      force?: boolean;
    }) {
      try {
        const row = await v2Fetch<Record<string, unknown>>(
          "/api/v2/orders/pre-orders",
          {
            method: "POST",
            body: JSON.stringify(body),
          },
        );
        return adaptV2PreOrder(row);
      } catch (err: any) {
        if (err?.status === 409 && err?.body) {
          throw new DuplicatePreOrderError(
            err.body.message,
            err.body.existingPreOrder,
            err.body.count,
          );
        }
        throw err;
      }
    },

    /**
     * Apply the same delivery_date to N pre-orders in one server transaction.
     * Server returns the actually-updated id list and a list of ids it
     * silently skipped (no access via owner/manager/group). Pass `null` to
     * clear the date.
     */
    async batchUpdateDeliveryDate(params: {
      ids: string[];
      deliveryDate: string | null;
    }): Promise<{
      updated: number;
      updatedIds: string[];
      skippedIds: string[];
    }> {
      return v2Fetch<{
        updated: number;
        updatedIds: string[];
        skippedIds: string[];
      }>("/api/v2/orders/pre-orders/batch-delivery-date", {
        method: "PATCH",
        body: JSON.stringify({
          ids: params.ids,
          delivery_date: params.deliveryDate,
        }),
      });
    },

    async batchMerge(
      sourceIds: string[],
      overrides?: {
        mergedId?: string;
        flowIdMapping?: Array<{ sourceFlowId: string; newFlowId: string }>;
      },
    ): Promise<ReturnType<typeof adaptV2PreOrder>> {
      try {
        const row = await v2Fetch<Record<string, unknown>>(
          "/api/v2/orders/pre-orders/batch-merge",
          {
            method: "POST",
            body: JSON.stringify({
              sourceIds,
              ...(overrides?.mergedId ? { mergedId: overrides.mergedId } : {}),
              ...(overrides?.flowIdMapping
                ? { flowIdMapping: overrides.flowIdMapping }
                : {}),
            }),
          },
        );
        return adaptV2PreOrder(row);
      } catch (err: any) {
        if (err?.status === 422 && err?.body?.error) {
          throw new BatchMergeError(
            err.body.error,
            err.body.message ?? "Batch merge failed",
            err.body.details ?? null,
          );
        }
        throw err;
      }
    },

    /**
     * POST /api/v2/orders/pre-orders/:id/retry-export
     *
     * Forces M3 to mirror local state exactly: delete every M3 line on
     * the order, re-create from local flows. ORNO is preserved. Used by
     * the user-facing Retry button when a previous export left M3 and
     * local out of sync.
     *
     * 202 → ConfirmConsumer picked up the work; SSE will flip the row
     *       to success or failed when M3 responds.
     * 409 + `error: "cannot_retry_no_reference"` → row has never been
     *       exported. Caller should use the regular Confirm path.
     * 409 + `error: "export_in_progress"` → another export is in-flight.
     */
    async retryExport(preOrderId: string) {
      return v2Fetch<{ preOrder: Record<string, unknown> }>(
        `/api/v2/orders/pre-orders/${preOrderId}/retry-export`,
        { method: "POST" },
      );
    },
  },
};
